import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface AnalyticsSetting {
  setting_key: string;
  setting_value: string | null;
  is_enabled: boolean;
}

const AnalyticsScripts = () => {
  const { data: settings } = useQuery({
    queryKey: ['analytics-settings-public'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('analytics_settings')
        .select('setting_key, setting_value, is_enabled');
      
      if (error) {
        console.error('Failed to fetch analytics settings:', error);
        return [];
      }
      return data as AnalyticsSetting[];
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  useEffect(() => {
    if (!settings) return;

    const enabledSettings = settings.filter(s => s.is_enabled && s.setting_value?.trim());

    enabledSettings.forEach(setting => {
      const existingElement = document.querySelector(`[data-analytics="${setting.setting_key}"]`);
      if (existingElement) return; // Already injected

      switch (setting.setting_key) {
        case 'google_analytics_id':
          injectGoogleAnalytics(setting.setting_value!);
          break;
        case 'google_tag_manager_id':
          injectGoogleTagManager(setting.setting_value!);
          break;
        case 'facebook_pixel_id':
          injectFacebookPixel(setting.setting_value!);
          break;
        case 'hotjar_id':
          injectHotjar(setting.setting_value!);
          break;
        case 'clarity_id':
          injectClarity(setting.setting_value!);
          break;
        case 'google_site_verification':
          injectVerificationMeta('google-site-verification', setting.setting_value!);
          break;
        case 'bing_site_verification':
          injectVerificationMeta('msvalidate.01', setting.setting_value!);
          break;
        case 'pinterest_verification':
          injectVerificationMeta('p:domain_verify', setting.setting_value!);
          break;
        case 'yandex_verification':
          injectVerificationMeta('yandex-verification', setting.setting_value!);
          break;
        default:
          // Custom tracking codes are not auto-injected for security
          break;
      }
    });
  }, [settings]);

  return null; // This component doesn't render anything
};

function injectVerificationMeta(name: string, content: string) {
  const meta = document.createElement('meta');
  meta.name = name;
  meta.content = content;
  meta.setAttribute('data-analytics', name.replace(/[:.]/g, '_'));
  document.head.appendChild(meta);
}

function injectGoogleAnalytics(measurementId: string) {
  // Google Analytics 4 script
  const script1 = document.createElement('script');
  script1.async = true;
  script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script1.setAttribute('data-analytics', 'google_analytics_id');
  document.head.appendChild(script1);

  const script2 = document.createElement('script');
  script2.setAttribute('data-analytics', 'google_analytics_id_config');
  script2.textContent = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${measurementId}');
  `;
  document.head.appendChild(script2);
}

function injectGoogleTagManager(containerId: string) {
  // GTM script for head
  const script = document.createElement('script');
  script.setAttribute('data-analytics', 'google_tag_manager_id');
  script.textContent = `
    (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
    new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
    j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
    'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
    })(window,document,'script','dataLayer','${containerId}');
  `;
  document.head.appendChild(script);

  // GTM noscript for body (iframe fallback)
  const noscript = document.createElement('noscript');
  noscript.setAttribute('data-analytics', 'google_tag_manager_id_noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body.insertBefore(noscript, document.body.firstChild);
}

function injectFacebookPixel(pixelId: string) {
  const script = document.createElement('script');
  script.setAttribute('data-analytics', 'facebook_pixel_id');
  script.textContent = `
    !function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)}(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${pixelId}');
    fbq('track', 'PageView');
  `;
  document.head.appendChild(script);

  // Add noscript pixel
  const noscript = document.createElement('noscript');
  noscript.setAttribute('data-analytics', 'facebook_pixel_id_noscript');
  const img = document.createElement('img');
  img.height = 1;
  img.width = 1;
  img.style.display = 'none';
  img.src = `https://www.facebook.com/tr?id=${pixelId}&ev=PageView&noscript=1`;
  noscript.appendChild(img);
  document.head.appendChild(noscript);
}

function injectHotjar(siteId: string) {
  const script = document.createElement('script');
  script.setAttribute('data-analytics', 'hotjar_id');
  script.textContent = `
    (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:${siteId},hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
  `;
  document.head.appendChild(script);
}

function injectClarity(projectId: string) {
  const script = document.createElement('script');
  script.setAttribute('data-analytics', 'clarity_id');
  script.textContent = `
    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", "${projectId}");
  `;
  document.head.appendChild(script);
}

export default AnalyticsScripts;
